/**
 * ListingHeader Component (Server)
 *
 * Diamond Architecture: Server Component for SEO.
 * Displays price, location, and key property stats.
 * Receives plain ViewModel types — no proto dependency.
 */
import { SaveButton } from '@/shared/components/listings';
import { useLocale, useTranslations } from '@/shared/i18n';
import { Link } from '@/shared/i18n/routing';
import { detailPriceSlotForIntent } from '@/shared/lib/listings/detail-price-slot';
import type { ListingDetailData } from '@/shared/lib/types/listing-detail';
import { formatPrice } from '@letsgethome/lib/currency';
import { DetailPriceSlot, listingPriceModes, type ListingPriceModes } from '@letsgethome/lib/wasm';
import { cents, plaintextString } from '@letsgethome/types/branded';
import { Badge } from '@letsgethome/ui/badge';

type Props = {
  listing: ListingDetailData;
};

/** The chosen slot's priced value — never the other mode's (STRICT). */
function slotPriceCents(
  slot: DetailPriceSlot | undefined,
  modes: ListingPriceModes
): number | undefined {
  if (slot === DetailPriceSlot.Rent) return modes.rentPriceCents;
  if (slot === DetailPriceSlot.Sale) return modes.salePriceCents;
  return undefined;
}

export function ListingHeader({ listing }: Props) {
  const t = useTranslations('listings.detail');
  const locale = useLocale();

  // DETAIL page: WHICH mode the page binds is the Rust snap machine
  // (`detailPriceSlot` — snaps `defaultIntentValue` onto the OFFERED flags;
  // shared verbatim with iOS/Android/backend), and whether that mode carries
  // a showable value is `listingPriceModes` (offered AND positively priced).
  // An offered-but-unpriced mode hides the price row (upstream data bug)
  // instead of silently binding the other mode — the sale-price-in-a-rent-
  // slot class stays unrepresentable.
  const modes = listingPriceModes(
    listing.forRent,
    listing.forSale,
    listing.rentPriceCents,
    listing.salePriceCents
  );
  const slot = detailPriceSlotForIntent(
    listing.defaultIntentValue,
    listing.forRent,
    listing.forSale
  );
  const isRent = slot === DetailPriceSlot.Rent;
  const priceCents = slotPriceCents(slot, modes);

  return (
    <div className="space-y-4">
      {/* Price Row */}
      <div className="flex items-start justify-between">
        <div>
          {priceCents !== undefined && (
            <div className="flex items-baseline gap-2">
              <h1 className="text-3xl font-bold">{formatPrice(cents(priceCents), locale)}</h1>
              {isRent && <span className="text-lg text-muted-foreground">{t('perMonth')}</span>}
            </div>
          )}

          {/* Transaction Type Badges */}
          <div className="mt-2 flex gap-2">
            {listing.forRent && <Badge variant="secondary">{t('forRent')}</Badge>}
            {listing.forSale && <Badge variant="secondary">{t('forSale')}</Badge>}
          </div>
        </div>

        {/* Save Button */}
        <SaveButton saveable={{ kind: 'listing', id: listing.id }} serverSaved={listing.saved} />
      </div>

      {/* Location */}
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">
          {/* SEO F5: the estate name becomes a LINK when the estate has a
              page. `estateHref` exists only when the backend could build a
              resolving target, so this is never a link to a 404 — and it is
              what closes the listing→estate direction of the crawl mesh
              (the estate→listing direction shipped in #3548). */}
          {listing.estateName && listing.estateHref ? (
            <Link
              href={listing.estateHref}
              prefetch="intent"
              className="underline-offset-2 hover:underline"
            >
              {plaintextString(listing.estateName)}
            </Link>
          ) : (
            plaintextString(listing.estateName || '') || t('unknownLocation')
          )}
        </h2>
        <p className="text-muted-foreground">
          {plaintextString(listing.fullAddress || listing.districtName || '')}
        </p>
      </div>

      {/* Reference Number */}
      {listing.refNumber && (
        <p className="text-sm text-muted-foreground">
          {t('ref')}: {plaintextString(listing.refNumber)}
        </p>
      )}
    </div>
  );
}
